import { useState, useEffect, useRef, Fragment } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useConfirm } from '../context/ConfirmContext';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { BACKEND_URL } from '../config';

const DEFAULT_DEVICE_IMAGE = `${process.env.PUBLIC_URL || ''}/assets/images/dio-iot-device.png`;

const getPhotoUrl = (item) => {
  if (item?.photo_url || item?.logo_url) return `${BACKEND_URL}${item.photo_url || item.logo_url}`;
  return DEFAULT_DEVICE_IMAGE;
};

const normalizeDeviceType = (t) => String(t || '').trim().toUpperCase();

const Devices = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { confirm } = useConfirm();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [selectedLocationFilter, setSelectedLocationFilter] = useState('');

  const [selectedDatalogger, setSelectedDatalogger] = useState(null);

  const [dataLoggers, setDataLoggers] = useState([]);
  const [inverters, setInverters] = useState([]);
  const [energyMeters, setEnergyMeters] = useState([]);
  const [s475Devices, setS475Devices] = useState([]);

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isDarkMode, setIsDarkMode] = useState(document.documentElement.classList.contains('dark'));

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [addDeviceType, setAddDeviceType] = useState('');
  const [formData, setFormData] = useState({
    brand: '',
    serial_number: '',
    model: '',
    ip_address: '',
    name: '',
    company_id: '',
    data_logger_id: '',
    port: 502,
    unit_id: '',
    power_kw: '',
    location_name: '',
    s475_id: '',
    http_port: 8081,
    lat: '',
    lng: '',
    checkin_radius_meters: '',
    generator_liters_per_hour: '',
    generator_tracking_din: 'DIN0'
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const [selectedDevice, setSelectedDevice] = useState(null);
  const [currentDeviceIndex, setCurrentDeviceIndex] = useState(0);
  const [deviceView, setDeviceView] = useState('list');
  const [showDetails, setShowDetails] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [analysisPeriod, setAnalysisPeriod] = useState('day');
  const [showEdgePopup, setShowEdgePopup] = useState(false);
  const [edgePopupDevice, setEdgePopupDevice] = useState(null);

  const fileInputRef = useRef(null);
  const photoFileRef = useRef(null); // ref backup so save always sees the selected file
  const longPressTimerRef = useRef(null);

  const isSuperAdmin = user?.role === 'SUPERADMIN';

  // Brendet për dropdown: Data Logger & Inverter (Huawei i pari), Energy Meter (Schneider, Siemens); modeli mbetet me shkrim
  const DATA_LOGGER_INVERTER_BRANDS = ['Huawei', 'Sungrow', 'GoodWe', 'Growatt'];
  const ENERGY_METER_BRANDS = ['Schneider', 'Siemens'];
  const getBrandOptions = () => {
    if (addDeviceType === 'energymeter') return ENERGY_METER_BRANDS;
    if (addDeviceType === 'datalogger' || addDeviceType === 'inverter') return DATA_LOGGER_INVERTER_BRANDS;
    return [];
  };
  const brandOptions = getBrandOptions();
  const effectiveBrandOptions = (formData.brand && formData.brand.trim() && !brandOptions.includes(formData.brand)) ? [formData.brand.trim(), ...brandOptions] : brandOptions;

  const extractId = (v) => {
    if (!v) return '';
    if (typeof v === 'string' || typeof v === 'number') return String(v).trim();
    if (typeof v === 'object') {
      // Mongo style { $oid: "..." } ose { oid: "..." } ose { id: "..." }
      return String(v.$oid || v.oid || v.id || '').trim();
    }
    return String(v).trim();
  };

  const selectedCompanyId = extractId(selectedCompany?.id);

  const updateCompanyInUrl = (companyId) => {
    const params = new URLSearchParams(searchParams);
    const id = companyId != null ? String(companyId).trim() : '';
    if (id && id !== 'undefined' && id !== 'null') params.set('company', id);
    else params.delete('company');
    navigate({ pathname: '/devices', search: params.toString() }, { replace: true });
  };

  const switchCompany = (company) => {
    setSelectedCompany(company || null);
    setShowCompanyDropdown(false);
    setSelectedDatalogger(null);

    // reset form selection so dropdown mos me mbet prej kompanisë tjetër
    setFormData((prev) => ({
      ...prev,
      company_id: extractId(company?.id),
      data_logger_id: ''
    }));

    if (company?.id) updateCompanyInUrl(company.id);
  };

  useEffect(() => {
    fetchData();

    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);

    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    const tabParam = searchParams.get('tab');
    if (tabParam === 'inverter') setActiveTab('inverter');
    else if (tabParam === 'datalogger') setActiveTab('datalogger');
    else if (tabParam === 'ems') setActiveTab('ems');

    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const companyParam = searchParams.get('company');
    if (!companyParam || companyParam === 'undefined' || companyParam === 'null') return;
    if (companies.length === 0) return;
    const company = companies.find(c => String(c.id) === String(companyParam));
    if (company) setSelectedCompany(company);
  }, [searchParams, companies]);

  useEffect(() => {
    if (activeTab !== 'inverter') setSelectedDatalogger(null);
  }, [activeTab]);

  // ESC = back: close modal/popup first, else go back
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (showEdgePopup) setShowEdgePopup(false);
      else if (showAddModal) setShowAddModal(false);
      else navigate(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showEdgePopup, showAddModal, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };

      const companiesRes = await axios.get(`${BACKEND_URL}/api/companies`, { headers });
      const companiesData = Array.isArray(companiesRes.data) ? companiesRes.data : [];
      setCompanies(companiesData);

      const companyParam = searchParams.get('company');
      const invalidCompanyParam = !companyParam || companyParam === 'undefined' || companyParam === 'null';
      const companyIdFromUrl = invalidCompanyParam ? null : companyParam;
      const selected = companyIdFromUrl
        ? companiesData.find(c => String(c.id) === String(companyIdFromUrl))
        : companiesData[0];

      setSelectedCompany(selected || null);

      const selectedId = selected?.id ? String(selected.id) : '';

      if (selected && invalidCompanyParam) {
        const params = new URLSearchParams(searchParams);
        params.set('company', String(selected.id));
        navigate({ pathname: '/devices', search: params.toString() }, { replace: true });
      }

      setDataLoggers([]);
      setInverters([]);
      setEnergyMeters([]);
      try {
        const s475Url = selectedId ? `${BACKEND_URL}/api/s475/devices?company_id=${encodeURIComponent(selectedId)}` : `${BACKEND_URL}/api/s475/devices`;
        const s475Res = await axios.get(s475Url, { headers });
        setS475Devices(Array.isArray(s475Res.data) ? s475Res.data : []);
      } catch {
        setS475Devices([]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };


  const cardShadowClass =
    "shadow-[6px_6px_12px_rgba(0,0,0,0.08),-6px_-6px_12px_rgba(255,255,255,0.9)] dark:shadow-none";

  const getCompanyDataLoggers = () => {
    if (!selectedCompanyId) return [];
    return dataLoggers.filter(dl => extractId(dl.company_id) === selectedCompanyId);
  };
  // ✅ FIX: 'INVERTER' jo 'inverter'
  const getCompanyInverters = () => {
    if (!selectedCompanyId) return [];
    return inverters.filter(inv =>
      extractId(inv.company_id) === selectedCompanyId &&
      normalizeDeviceType(inv.device_type) === 'INVERTER'
    );
  };

  const getCompanyEnergyMeters = () => {
    if (!selectedCompanyId) return [];
    const t = (x) => normalizeDeviceType(x);
    return energyMeters.filter(em =>
      extractId(em.company_id) === selectedCompanyId &&
      (t(em.device_type) === 'ENERGY_METER' || t(em.device_type) === 'ENERGYMETER')
    );
  };

  const getAllDevices = () => [
    ...s475Devices.map(d => ({ ...d, type: 's475', name: d.location_name || d.label, id: d.id }))
  ];

  const getFilteredDevices = () => {
    const all = getAllDevices();
    if (!selectedLocationFilter) return all;
    return all.filter(d => String(d.id) === String(selectedLocationFilter));
  };

  // Status nga collections reader – datalogger: plant_status_text, inverter: device_status_text (join në backend)
  const _getStatusLabelForStyle = (device) => {
    const plantText = device?.plant_status_text ?? device?.plantStatusText;
    const deviceText = device?.device_status_text ?? device?.deviceStatusText;
    const isDl = device?.type === 'datalogger' || (device?.device_type && String(device.device_type).toUpperCase() === 'DATA_LOGGER');
    const isInv = device?.type === 'inverter' || (device?.device_type && String(device.device_type).toUpperCase() === 'INVERTER');
    if (isDl && plantText) return String(plantText).toUpperCase();
    if (isInv && deviceText) return String(deviceText).toUpperCase();
    return (device?.status || '').toUpperCase();
  };

  // Ngjyrë pike sipas status – Running=green, Idle=yellow, Standby=orange, Offline=red. Energy meter: gjithmonë jeshil (Online).
  const getDeviceStatusStyle = (device) => {
    if (device?.type === 's475') return { textClass: 'text-[#22c55e]', dotClass: 'bg-[#22c55e]' };
    const isEm = device?.type === 'energymeter' || (device?.device_type && String(device.device_type).toUpperCase() === 'ENERGY_METER');
    if (isEm) return { textClass: 'text-[#22c55e]', dotClass: 'bg-[#22c55e]' };
    const label = _getStatusLabelForStyle(device);
    const s = (device?.status || '').toUpperCase();
    if (label.includes('RUNNING') || s === 'ONLINE') return { textClass: 'text-[#22c55e]', dotClass: 'bg-[#22c55e]' };  // jeshil
    if (label.includes('IDLE') || s === 'IDLE') return { textClass: 'text-amber-400', dotClass: 'bg-amber-400' };       // qëndruese
    if (label.includes('STANDBY') || s === 'STANDBY') return { textClass: 'text-orange-500', dotClass: 'bg-orange-500' }; // portokalli
    if (label.includes('FAULT') || label.includes('SHUTDOWN') || label.includes('ERROR')) return { textClass: 'text-red-500', dotClass: 'bg-red-500' };
    if (label.includes('OFFLINE') || s === 'OFFLINE') return { textClass: 'text-red-500', dotClass: 'bg-red-500' };      // kuq
    if (s === 'ONLINE') return { textClass: 'text-[#22c55e]', dotClass: 'bg-[#22c55e]' };
    if (s === 'STANDBY' || s === 'IDLE') return { textClass: 'text-orange-500', dotClass: 'bg-orange-500' };
    return { textClass: 'text-red-500', dotClass: 'bg-red-500' };
  };

  // Status nga collections reader – datalogger: plant_status_text, inverter: device_status_text (join në backend)
  // Energy meter: gjithmonë "Online". Për inverterët: heqim "No irradiation" nga shfaqja.
  const getDeviceStatusText = (device) => {
    if (!device) return null;
    if (device.type === 's475') return t('online');
    const isEm = device.type === 'energymeter' || (device.device_type && String(device.device_type).toUpperCase() === 'ENERGY_METER');
    if (isEm) return t('online');
    const plantText = device.plant_status_text ?? device.plantStatusText;
    const deviceText = device.device_status_text ?? device.deviceStatusText;
    const isDl = device.type === 'datalogger' || (device.device_type && String(device.device_type).toUpperCase() === 'DATA_LOGGER');
    const isInv = device.type === 'inverter' || (device.device_type && String(device.device_type).toUpperCase() === 'INVERTER');
    if (isDl && plantText) return String(plantText).trim();
    if (isInv && deviceText) {
      const txt = String(deviceText).trim();
      if (txt.toLowerCase().includes('no irradiation') || txt.includes('40960')) return t('standby');
      return txt;
    }
    return null;
  };

  // Fallback kur nuk ka plant_status_text / device_status_text nga reader
  const getDefaultStatusLabel = (device) => {
    const s = (device?.status || '').toUpperCase();
    if (s === 'ONLINE') return 'Online';
    if (s === 'STANDBY' || s === 'IDLE') return 'Standby';
    return 'Offline';
  };

  const handleTouchStart = (device) => {
    if (!isSuperAdmin) return;
    if (device?.type === 's475') return;
    longPressTimerRef.current = setTimeout(() => {
      openEditModal(device);
    }, 3000);
  };

  const handleTouchEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const openEditModal = (device) => {
    setEditingDevice(device);

    const normalized = String(device?.type || device?.device_type || '').trim().toLowerCase();

    const type =
      normalized === 's475'
        ? 's475'
        : normalized === 'inverter'
          ? 'inverter'
          : normalized === 'energymeter' || normalized === 'energy_meter' || normalized === 'energy-meter'
            ? 'energymeter'
            : 'datalogger';

    setAddDeviceType(type);

    if (type === 's475') {
      setFormData((prev) => ({
        ...prev,
        location_name: device.location_name || device.label || '',
        s475_id: device.id || '',
        port: device.port != null ? device.port : 801,
        http_port: device.http_port != null ? device.http_port : 8081,
        unit_id: device.unit_id != null && device.unit_id !== '' ? device.unit_id : device.id ?? '',
        lat: device.lat != null && device.lat !== '' ? String(device.lat) : '',
        lng: device.lng != null && device.lng !== '' ? String(device.lng) : '',
        checkin_radius_meters: device.checkin_radius_meters != null && device.checkin_radius_meters !== '' ? String(device.checkin_radius_meters) : '',
        company_id: device.company_id || selectedCompanyId || '',
        generator_liters_per_hour:
          device.generator_liters_per_hour != null && device.generator_liters_per_hour !== ''
            ? String(device.generator_liters_per_hour)
            : '',
        generator_tracking_din: device.generator_tracking_din === 'DIN1' ? 'DIN1' : 'DIN0'
      }));
    } else {
      setFormData({
        brand: device.brand || '',
        serial_number: device.serial_number || '',
        model: device.model || '',
        ip_address: device.ip_address || '',
        name: device.name || '',
        company_id: device.company_id || selectedCompanyId || '',
        data_logger_id: device.data_logger_id || '',
        port: device.port != null ? device.port : 502,
        unit_id: device.unit_id != null && device.unit_id !== '' ? device.unit_id : '',
        power_kw: (device.power_kw ?? device.rated_power_kw) != null ? String(device.power_kw ?? device.rated_power_kw) : '',
        location_name: '',
        s475_id: '',
        http_port: 8081,
        lat: '',
        lng: '',
        checkin_radius_meters: '',
        generator_liters_per_hour: '',
        generator_tracking_din: 'DIN0'
      });
    }

    setPhotoFile(null);
    photoFileRef.current = null;
    setShowAddModal(true);
    // #region agent log
    fetch('http://127.0.0.1:7244/ingest/d43d6308-ec48-474e-90c9-52e63fae8135', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'Devices.js:openEditModal', message: 'Edit modal opened', data: { deviceType: type, deviceId: device?.id, isMobile: window.innerWidth < 768 }, timestamp: Date.now(), sessionId: 'debug-session', hypothesisId: 'H1' }) }).catch(() => { });
    // #endregion
  };

  const openAddModal = () => {
    setEditingDevice(null);
    setAddDeviceType('s475');
    setFormData({
      brand: '',
      serial_number: '',
      model: '',
      ip_address: '',
      name: '',
      company_id: selectedCompanyId || '',
      data_logger_id: '',
      port: 801,
      unit_id: '',
      power_kw: '',
      location_name: '',
      s475_id: '',
      http_port: 8081,
      lat: '',
      lng: '',
      checkin_radius_meters: '',
      generator_liters_per_hour: '',
      generator_tracking_din: 'DIN0'
    });
    setPhotoFile(null);
    photoFileRef.current = null;
    setShowAddModal(true);
  };

  // ✅ auto-select datalogger vetëm kur zgjedh inverter dhe dropdown është bosh (energy meter nuk lidhet me data logger)
  useEffect(() => {
    if (!showAddModal) return;
    if (addDeviceType !== 'inverter') return;
    if (formData.data_logger_id) return;

    const dls = getCompanyDataLoggers();
    if (dls.length > 0) {
      setFormData((prev) => ({ ...prev, data_logger_id: String(dls[0].id) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddModal, addDeviceType, selectedCompanyId]);

  const handleDeleteDevice = async () => {
    if (!editingDevice) return;
    const confirmMsg = addDeviceType === 's475'
      ? 'Dëshironi ta fshini këtë pajisje S475?'
      : addDeviceType === 'datalogger'
        ? t('confirmDeleteDatalogger')
        : t('confirmDeleteDevice');
    const ok = await confirm({ title: t('confirmDeleteTitle') || 'Konfirmo fshirjen', message: confirmMsg, confirmLabel: t('delete') || 'Fshi', cancelLabel: t('cancel') || 'Jo' });
    if (!ok) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      if (addDeviceType === 's475') {
        await axios.delete(`${BACKEND_URL}/api/s475/devices/${editingDevice.id}`, { headers });
        toast.success(t('deviceDeleted'));
        setShowAddModal(false);
        setEditingDevice(null);
        await fetchData();
      } else {
        const endpoint = addDeviceType === 'datalogger'
          ? `/api/devices/data-loggers/${editingDevice.id}`
          : addDeviceType === 'inverter'
            ? `/api/devices/inverters/${editingDevice.id}`
            : `/api/devices/energy-meters/${editingDevice.id}`;

        await axios.delete(`${BACKEND_URL}${endpoint}`, { headers });
        toast.success(t('deviceDeleted'));
        setShowAddModal(false);
        setEditingDevice(null);
        await fetchData();
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const isNetworkError = !error.response || (error.message && String(error.message).toLowerCase().includes('network'));
      const fallback = isNetworkError ? 'Lidhja me serverin dështoi. Kontrolloni që backend-i të jetë i ndezur.' : 'Gabim gjatë fshirjes';
      const msg = isNetworkError ? fallback : (Array.isArray(detail) ? detail.map((d) => d.msg || d).join(', ') : (detail ?? error.message ?? 'Gabim gjatë fshirjes'));
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDevice = async () => {
    if (!addDeviceType) return;
    if (!selectedCompanyId) {
      toast.error('Zgjidh një kompani!');
      return;
    }

    if (addDeviceType === 's475') {
      const location_name = (formData.location_name || '').trim();
      const s475_id = (formData.s475_id || '').toString().trim();
      if (!location_name) {
        toast.error('Lokacioni kërkohet');
        return;
      }
      if (!s475_id) {
        toast.error('ID i pajisjes (numër) kërkohet');
        return;
      }
      const idNum = Number.parseInt(String(s475_id).trim(), 10);
      if (!Number.isFinite(idNum) || idNum < 1) {
        toast.error('ID duhet të jetë numër i plotë ≥ 1 (i njëjti për app dhe Modbus)');
        return;
      }
      setSaving(true);
      try {
        const token = localStorage.getItem('access_token');
        const headers = { Authorization: `Bearer ${token}` };
        const payload = {
          company_id: selectedCompanyId,
          location_name,
          id: String(idNum),
          port: Number(formData.port) || 801,
          http_port: Number(formData.http_port) || 8081,
          unit_id: idNum,
          lat: formData.lat !== '' && formData.lat != null ? parseFloat(formData.lat) : undefined,
          lng: formData.lng !== '' && formData.lng != null ? parseFloat(formData.lng) : undefined,
          checkin_radius_meters: formData.checkin_radius_meters !== '' && formData.checkin_radius_meters != null ? parseInt(formData.checkin_radius_meters, 10) : undefined,
          generator_liters_per_hour:
            formData.generator_liters_per_hour !== '' && formData.generator_liters_per_hour != null
              ? parseFloat(String(formData.generator_liters_per_hour).replace(',', '.'))
              : undefined,
          generator_tracking_din: formData.generator_tracking_din === 'DIN1' ? 'DIN1' : 'DIN0'
        };
        if (editingDevice) {
          await axios.put(`${BACKEND_URL}/api/s475/devices/${editingDevice.id}`, payload, { headers });
          toast.success('Pajisja S475 u përditësua!');
        } else {
          await axios.post(`${BACKEND_URL}/api/s475/devices`, payload, { headers });
          toast.success('Pajisja S475 u shtua!');
        }
        setShowAddModal(false);
        await fetchData();
      } catch (e) {
        const msg = e.response?.data?.detail || e.message || 'Gabim';
        toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
      } finally {
        setSaving(false);
      }
      return;
    }

    const fileToUpload = photoFile || photoFileRef.current;
    const deviceIdForLog = editingDevice?.id;
    // #region agent log
    fetch('http://127.0.0.1:7244/ingest/d43d6308-ec48-474e-90c9-52e63fae8135', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'Devices.js:handleSaveDeviceStart', message: 'Save device started', data: { hasPhotoFile: !!photoFile, hasRefFile: !!photoFileRef.current, fileToUpload: !!fileToUpload, deviceId: deviceIdForLog, editing: !!editingDevice }, timestamp: Date.now(), sessionId: 'debug-session', hypothesisId: 'H4' }) }).catch(() => { });
    // #endregion

    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };

      const endpoint =
        addDeviceType === 'datalogger'
          ? '/api/devices/data-loggers'
          : addDeviceType === 'inverter'
            ? '/api/devices/inverters'
            : '/api/devices/energy-meters';

      const port = formData.port != null && formData.port !== '' ? Number(formData.port) : 502;
      const unitId = formData.unit_id != null && formData.unit_id !== '' ? Number(formData.unit_id) : (addDeviceType === 'datalogger' ? 0 : undefined);
      const serialNumber = editingDevice ? (formData.serial_number || editingDevice.serial_number) : (formData.serial_number || formData.name || `${addDeviceType}-${Date.now()}`);
      const payload = {
        serial_number: serialNumber,
        name: formData.name || formData.brand || serialNumber,
        brand: formData.brand,
        model: formData.model,
        company_id: selectedCompanyId,
        port,
        ...(unitId !== undefined ? { unit_id: unitId } : {}),
        ...(addDeviceType === 'datalogger'
          ? { ip_address: formData.ip_address }
          : addDeviceType === 'energymeter'
            ? { ip_address: formData.ip_address || undefined }
            : { data_logger_id: formData.data_logger_id || getCompanyDataLoggers()[0]?.id }
        ),
        ...(addDeviceType === 'inverter' && formData.power_kw ? { power_kw: parseFloat(formData.power_kw), rated_power_kw: parseFloat(formData.power_kw) } : {})
      };

      if (addDeviceType === 'inverter' && !payload.data_logger_id) {
        toast.error('Zgjidh Data Logger!');
        setSaving(false);
        return;
      }

      let deviceId;
      if (editingDevice) {
        await axios.put(`${BACKEND_URL}${endpoint}/${editingDevice.id}`, payload, { headers });
        deviceId = editingDevice.id;
      } else {
        const response = await axios.post(`${BACKEND_URL}${endpoint}`, payload, { headers });
        deviceId = response.data?.id;
      }

      if (fileToUpload && deviceId) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', fileToUpload);

        const deviceTypeForUpload =
          addDeviceType === 'datalogger' ? 'data-logger' : addDeviceType === 'energymeter' ? 'energy-meter' : 'inverter';

        // Kur hapet nga localhost dhe BACKEND_URL është production, përdor backend lokal për ngarkim (shmang 405)
        const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
        const uploadBaseUrl = BACKEND_URL;
        const uploadUrl = `${uploadBaseUrl}/api/upload/photo/${deviceTypeForUpload}/${deviceId}`;
        // #region agent log
        fetch('http://127.0.0.1:7244/ingest/d43d6308-ec48-474e-90c9-52e63fae8135', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'Devices.js:photoUploadBefore', message: 'Before photo upload', data: { method: 'POST', uploadUrl, BACKEND_URL, deviceTypeForUpload, deviceId, hasToken: !!headers.Authorization }, timestamp: Date.now(), sessionId: 'debug-session', hypothesisId: 'H1' }) }).catch(() => { });
        // #endregion
        try {
          const uploadRes = await axios.post(uploadUrl, formDataUpload, {
            headers: { Authorization: headers.Authorization }
          });
          // #region agent log
          fetch('http://127.0.0.1:7244/ingest/d43d6308-ec48-474e-90c9-52e63fae8135', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'Devices.js:photoUploadSuccess', message: 'Photo upload OK', data: { status: uploadRes.status, photo_url: uploadRes.data?.photo_url }, timestamp: Date.now(), sessionId: 'debug-session', hypothesisId: 'H2' }) }).catch(() => { });
          // #endregion
          if (uploadRes.data?.photo_url) {
            console.log('Foto u ruajt:', uploadRes.data.photo_url);
          }
        } catch (uploadErr) {
          const status = uploadErr.response?.status;
          const detail = uploadErr.response?.data?.detail || uploadErr.message;
          const reqUrl = uploadErr.config?.url;
          const reqMethod = uploadErr.config?.method;
          // #region agent log
          fetch('http://127.0.0.1:7244/ingest/d43d6308-ec48-474e-90c9-52e63fae8135', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'Devices.js:photoUploadError', message: 'Photo upload failed', data: { status, detail, uploadUrl, reqUrl, reqMethod, BACKEND_URL }, timestamp: Date.now(), sessionId: 'debug-session', hypothesisId: 'H1' }) }).catch(() => { });
          // #endregion
          console.error('Gabim ngarkimi foto:', { status, detail, url: uploadUrl, backend: BACKEND_URL });
          const msg = status === 401
            ? 'Foto: nuk je i kyçur ose token i skaduar. Dil dhe kyçu përsëri.'
            : status === 404
              ? 'Foto: endpoint ose pajisja nuk u gjet. Kontrollo që backend-i është i përditësuar (nisje përsëri).'
              : status === 405
                ? 'Foto: Method Not Allowed. Backend-i duhet rinovuar (mbyll dhe nis përsëri serverin).'
                : status === 500
                  ? `Foto: gabim serveri: ${detail}`
                  : `Foto nuk u ruajt (${status || 'rrjet'}): ${detail}`;
          toast.error(msg);
          setSaving(false);
          return;
        }
      }

      await fetchData();
      setShowAddModal(false);
      toast.success(editingDevice ? 'Pajisja u përditësua!' : 'Pajisja u shtua!');
    } catch (error) {
      const isNetwork = !error.response || (error.message && String(error.message).toLowerCase().includes('network'));
      const status = error.response?.status;
      const detail = error.response?.data?.detail || error.message;
      console.error('Gabim ruajtje pajisje:', { status, detail });
      toast.error(isNetwork ? 'Lidhja me serverin dështoi. Kontrolloni që backend-i të jetë i ndezur.' : (status ? `[${status}] ${detail}` : detail) || 'Gabim');
    } finally {
      setSaving(false);
    }
  };

  const getMockInverterData = (device) => {
    const base = device?.installed_capacity_kw || device?.rated_power_kw || 5;
    return {
      pvPower: (base * 0.85).toFixed(3),
      todayYield: (base * 5.2).toFixed(1),
      totalYield: (base * 920).toFixed(1),
      voltage: { r: 230.5, s: 231.4, t: 230.5 },
      current: { r: 6.8, s: 6.8, t: 6.9 },
      power: { r: 1.567, s: 1.571, t: 1.540 }
    };
  };

  const getChartData = () =>
    analysisPeriod === 'day'
      ? Array.from({ length: 24 }, (_, i) => ({
        hour: i,
        value: i >= 6 && i <= 18 ? Math.sin((i - 6) * Math.PI / 12) * 50 + Math.random() * 10 : Math.random() * 2
      }))
      : Array.from({ length: 30 }, (_, i) => ({ day: i + 1, value: Math.random() * 100 + 50 }));

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-500">Duke ngarkuar...</p>
        </div>
      </Layout>
    );
  }

  // Mobile Detail View
  const MobileDetailView = () => {
    const devices = getFilteredDevices();
    const device = selectedDevice;
    const mockData = getMockInverterData(device);
    const chartData = getChartData();
    const maxValue = Math.max(...chartData.map(d => d.value));

    return (
      <div className="h-full flex flex-col bg-gray-100 dark:bg-gradient-to-b dark:from-slate-900 dark:to-black">
        <div className="p-4 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <div className="flex items-center justify-between mb-3">
            <select
              value={selectedCompany?.id || ''}
              onChange={(e) => {
                const company = companies.find(c => String(c.id) === String(e.target.value));
                switchCompany(company);
                setDeviceView('list');
              }}
              className="text-lg font-bold bg-transparent border-none text-gray-900 dark:text-white focus:outline-none cursor-pointer pr-6"
            >
              {companies.map(c => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
            <button onClick={() => setDeviceView('list')} className="text-gray-500 dark:text-white text-xl">✕</button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <span className="inline-block px-3 py-1 bg-green-500 text-white text-xs font-bold rounded mb-4">Normal</span>

          <div
            className="relative mb-4"
            onTouchStart={() => handleTouchStart(device)}
            onTouchEnd={handleTouchEnd}
            onMouseDown={() => handleTouchStart(device)}
            onMouseUp={handleTouchEnd}
            onMouseLeave={handleTouchEnd}
          >
            <div className={`bg-white dark:bg-[#1E293B] rounded-xl p-6 flex items-center justify-center h-40 ${cardShadowClass}`}>
              <img src={getPhotoUrl(device)} alt={device?.name || 'device'} className="max-h-32 object-contain" />
              {isSuperAdmin && <div className="absolute top-2 right-2 bg-gray-200 dark:bg-black/50 text-gray-600 dark:text-white text-[8px] px-1.5 py-0.5 rounded">3s edit</div>}
            </div>

            {devices.length > 1 && (
              <>
                <button
                  onClick={() => {
                    const idx = (currentDeviceIndex - 1 + devices.length) % devices.length;
                    setCurrentDeviceIndex(idx);
                    setSelectedDevice(devices[idx]);
                  }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-600 dark:text-white text-2xl bg-white/80 dark:bg-transparent rounded-full w-8 h-8 flex items-center justify-center"
                >
                  ‹
                </button>

                <button
                  onClick={() => {
                    const idx = (currentDeviceIndex + 1) % devices.length;
                    setCurrentDeviceIndex(idx);
                    setSelectedDevice(devices[idx]);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 dark:text-white text-2xl bg-white/80 dark:bg-transparent rounded-full w-8 h-8 flex items-center justify-center"
                >
                  ›
                </button>

                <div className="absolute bottom-2 right-4 text-gray-500 dark:text-gray-400 text-sm">
                  {currentDeviceIndex + 1}/{devices.length}
                </div>
              </>
            )}
          </div>

          <div className="text-center mb-4">
            <p className="text-gray-600 dark:text-gray-400 text-sm">{device?.serial_number} ›</p>
            <p className="text-gray-400 dark:text-gray-500 text-xs">Updated 1 minute ago</p>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className={`bg-white dark:bg-[#1E293B] rounded-xl p-4 ${cardShadowClass}`}>
              <div className="relative w-24 h-24 mx-auto mb-2">
                <svg className="w-24 h-24 transform -rotate-90">
                  <circle cx="48" cy="48" r="40" stroke="#e5e7eb" className="dark:stroke-gray-700" strokeWidth="8" fill="none" />
                  <circle cx="48" cy="48" r="40" stroke="#3b82f6" strokeWidth="8" fill="none" strokeDasharray={`${(mockData.pvPower / 10) * 251} 251`} strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-xl font-bold text-gray-900 dark:text-white">{mockData.pvPower}</span>
                  <span className="text-[10px] text-gray-500 dark:text-gray-400">kW</span>
                </div>
              </div>
              <p className="text-center text-xs text-gray-500 dark:text-gray-400">PV Power</p>
            </div>

            <div className="space-y-3">
              <div className={`bg-white dark:bg-[#1E293B] rounded-xl p-3 ${cardShadowClass}`}>
                <p className="text-xs text-gray-500 dark:text-gray-400">Today PV Yield</p>
                <p className="text-xl font-bold text-cyan-500">{mockData.todayYield}<span className="text-sm ml-1">kWh</span></p>
              </div>
              <div className={`bg-white dark:bg-[#1E293B] rounded-xl p-3 ${cardShadowClass}`}>
                <p className="text-xs text-gray-500 dark:text-gray-400">Total PV Yield</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{(mockData.totalYield / 1000).toFixed(1)}<span className="text-sm ml-1">MWh</span></p>
              </div>
            </div>
          </div>

          <div className={`bg-white dark:bg-[#1E293B] rounded-xl mb-3 ${cardShadowClass}`}>
            <button onClick={() => setShowDetails(!showDetails)} className="w-full flex items-center justify-between p-4">
              <span className="font-medium text-gray-900 dark:text-white">Detailed Parameters</span>
              <span className="text-gray-400">{showDetails ? '∧' : '›'}</span>
            </button>
            {showDetails && (
              <div className="px-4 pb-4">
                <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">AC Information</p>
                  <div className="grid grid-cols-4 gap-2 text-xs text-gray-700 dark:text-gray-300">
                    <div></div><div className="text-gray-500">V</div><div className="text-gray-500">A</div><div className="text-gray-500">kW</div>
                    {['R', 'S', 'T'].map(p => (
                      <Fragment key={p}>
                        <div className="font-medium">{p}</div>
                        <div>{mockData.voltage[p.toLowerCase()]}</div>
                        <div>{mockData.current[p.toLowerCase()]}</div>
                        <div>{mockData.power[p.toLowerCase()]}</div>
                      </Fragment>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={`bg-white dark:bg-[#1E293B] rounded-xl ${cardShadowClass}`}>
            <button onClick={() => setShowAnalysis(!showAnalysis)} className="w-full flex items-center justify-between p-4">
              <span className="font-medium text-gray-900 dark:text-white">Analysis Graph</span>
              <span className="text-gray-400">{showAnalysis ? '∧' : '∨'}</span>
            </button>
            {showAnalysis && (
              <div className="px-4 pb-4">
                <div className="flex gap-2 mb-4">
                  {['day', 'month', 'year', 'lifetime'].map(p => (
                    <button
                      key={p}
                      onClick={() => setAnalysisPeriod(p)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium ${analysisPeriod === p ? 'bg-green-500 text-white' : 'bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300'}`}
                    >
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </button>
                  ))}
                </div>
                <div className="flex items-center justify-center gap-4 mb-4">
                  <button className="text-gray-400">◀</button>
                  <span className="text-sm text-gray-700 dark:text-white">2026-01-22</span>
                  <button className="text-gray-400">▶</button>
                </div>
                <div className="h-40 flex items-end gap-px">
                  {chartData.map((d, i) => (
                    <div key={i} className="flex-1 bg-gradient-to-t from-cyan-600 to-cyan-400 rounded-t" style={{ height: `${(d.value / maxValue) * 100}%` }}></div>
                  ))}
                </div>
                <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                  <span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>24:00</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const MobileListView = () => {
    const devices = getFilteredDevices();
    return (
      <div className="h-full flex flex-col bg-[#f5f5f5] dark:bg-gradient-to-b dark:from-[#0f172a] dark:to-black">
        <div className="p-4 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-[#0f172a]">
          <div className="flex items-center justify-between gap-3">
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-[#334155] text-left min-w-[160px] hover:border-cyan-500/50 transition-colors"
              >
                <span className="text-sm font-bold text-gray-900 dark:text-white truncate">
                  {selectedCompany?.name || (companies.length === 0 && !loading ? 'Nuk ka kompani' : 'Loading...')}
                </span>
                <svg className={`w-4 h-4 text-gray-500 dark:text-gray-400 shrink-0 transition-transform ${showCompanyDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showCompanyDropdown && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white dark:bg-[#1e293b] rounded-xl shadow-lg z-50 overflow-hidden border border-gray-200 dark:border-[#334155]">
                  {companies.map(company => (
                    <button
                      key={company.id}
                      type="button"
                      onClick={() => switchCompany(company)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-[#2d3a4f] transition ${String(selectedCompany?.id) === String(company.id)
                        ? 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-600 dark:text-cyan-400 font-medium'
                        : 'text-gray-700 dark:text-white'
                        }`}
                    >
                      {company.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {isSuperAdmin && (
              <button onClick={openAddModal} className="w-7 h-7 shrink-0 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center text-white text-sm font-medium" aria-label={t('addDevice')}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pt-6 pb-4">
          <div className="grid grid-cols-2 gap-3">
            {devices.map((device, index) => (
              <div
                key={device.id}
                data-testid={`device-card-${device.id}`}
                onClick={() => {
                  if (device.type === 's475') navigate(`/devices/s475/${device.id}`);
                  else if (device.type === 'datalogger') navigate(`/devices/${device.id}`);
                  else if (device.type === 'inverter') navigate(`/devices/${device.data_logger_id}/inverter/${device.id}`);
                  else if (device.type === 'energymeter') navigate(`/devices/${device.data_logger_id || 'standalone'}/energy-meter/${device.id}`);
                }}
                onTouchStart={() => handleTouchStart(device)}
                onTouchEnd={handleTouchEnd}
                onMouseDown={() => handleTouchStart(device)}
                onMouseUp={handleTouchEnd}
                onMouseLeave={handleTouchEnd}
                className={`relative bg-white dark:bg-[#1e293b] rounded-xl p-3 cursor-pointer transition ${cardShadowClass}`}
              >
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); if (device.type === 's475' && isSuperAdmin) openEditModal(device); else { setEdgePopupDevice(device); setShowEdgePopup(true); } }}
                  className="absolute top-2 right-2 w-7 h-7 rounded-full bg-gray-200 dark:bg-slate-600 hover:bg-gray-300 dark:hover:bg-slate-500 flex items-center justify-center text-gray-600 dark:text-gray-300 text-xs font-medium z-10"
                  aria-label="Të dhënat e pajisjes"
                >
                  ℹ
                </button>
                <div className="w-full h-20 mb-3 rounded-lg overflow-hidden bg-white dark:bg-[#1e293b] flex items-center justify-center">
                  <img src={getPhotoUrl(device)} alt={device.name || 'Paisje'} className="max-h-full max-w-full object-contain" onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }} />
                </div>

                <h4 className="font-semibold text-sm text-gray-900 dark:text-white mb-1.5">
                  {device.type === 's475' ? (device.label || device.name) : device.type === 'datalogger' ? device.name : device.type === 'energymeter' ? (device.name || device.model || `Energy Meter-${index + 1}`) : (device.model || device.name || `Inverter-${index + 1}`)}
                </h4>

                <div className="flex items-center gap-2 text-xs flex-wrap">
                  {(() => {
                    const style = getDeviceStatusStyle(device);
                    const statusLabel = getDeviceStatusText(device) || getDefaultStatusLabel(device);
                    return (
                      <span className={`flex items-center gap-1 ${style.textClass}`}>
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${style.dotClass}`}></span>
                        <span className="truncate" title={statusLabel}>{statusLabel}</span>
                      </span>
                    );
                  })()}
                  {device.type !== 's475' && (
                    <>
                      <span className="text-gray-400 shrink-0">|</span>
                      <span className="text-gray-500 dark:text-gray-400 shrink-0">
                        {device.grid_status === 'ON_GRID' ? 'ON GRID' : 'OFF GRID'}
                      </span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {devices.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p>{t('noDevices')}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const WebListView = () => {
    const devices = getFilteredDevices();
    const allDevicesForFilter = getAllDevices();
    const selectedLocationLabel = selectedLocationFilter
      ? (allDevicesForFilter.find(d => String(d.id) === String(selectedLocationFilter))?.location_name || allDevicesForFilter.find(d => String(d.id) === String(selectedLocationFilter))?.label || selectedLocationFilter)
      : 'Të gjitha';
    return (
      <div className="h-full flex flex-col bg-gray-100 dark:bg-gray-900">
        <div className="p-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap items-center gap-3">
            {/* Dropdown Kompani – stil si Dashboard */}
            <div className="relative">
              <button
                type="button"
                onClick={() => { setShowCompanyDropdown(!showCompanyDropdown); setShowLocationDropdown(false); }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-[#334155] text-left min-w-[180px] hover:border-cyan-500/50 transition-colors"
              >
                <span className="text-sm font-bold text-gray-900 dark:text-white truncate">
                  {selectedCompany?.name || (companies.length === 0 && !loading ? 'Nuk ka kompani' : 'Loading...')}
                </span>
                <svg className={`w-4 h-4 text-gray-500 shrink-0 transition-transform ${showCompanyDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showCompanyDropdown && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white dark:bg-[#1e293b] rounded-xl shadow-lg z-50 overflow-hidden border border-gray-200 dark:border-[#334155]">
                  {companies.map((company) => (
                    <button
                      key={company.id}
                      type="button"
                      onClick={() => { switchCompany(company); setShowCompanyDropdown(false); }}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-[#2d3a4f] transition ${String(selectedCompany?.id) === String(company.id) ? 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-600 dark:text-cyan-400 font-medium' : 'text-gray-700 dark:text-white'}`}
                    >
                      {company.name}
                    </button>
                  ))}
                  {companies.length === 0 && <div className="px-4 py-3 text-gray-500 text-sm">Nuk ka kompani</div>}
                </div>
              )}
            </div>
            {/* Dropdown Lokacioni – Të gjitha ose një pajisje */}
            <div className="relative">
              <button
                type="button"
                onClick={() => { setShowLocationDropdown(!showLocationDropdown); setShowCompanyDropdown(false); }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-[#334155] text-left min-w-[160px] hover:border-cyan-500/50 transition-colors"
              >
                <span className="text-sm font-bold text-gray-900 dark:text-white truncate">{selectedLocationLabel}</span>
                <svg className={`w-4 h-4 text-gray-500 shrink-0 transition-transform ${showLocationDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showLocationDropdown && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-white dark:bg-[#1e293b] rounded-xl shadow-lg z-50 overflow-hidden border border-gray-200 dark:border-[#334155]">
                  <button type="button" onClick={() => { setSelectedLocationFilter(''); setShowLocationDropdown(false); }} className={`w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-[#2d3a4f] transition ${!selectedLocationFilter ? 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-600 dark:text-cyan-400 font-medium' : 'text-gray-700 dark:text-white'}`}>
                    Të gjitha
                  </button>
                  {allDevicesForFilter.map((d) => (
                    <button key={d.id} type="button" onClick={() => { setSelectedLocationFilter(d.id); setShowLocationDropdown(false); }} className={`w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-[#2d3a4f] transition ${String(selectedLocationFilter) === String(d.id) ? 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-600 dark:text-cyan-400 font-medium' : 'text-gray-700 dark:text-white'}`}>
                      {d.location_name || d.label || d.id}
                    </button>
                  ))}
                  {allDevicesForFilter.length === 0 && <div className="px-4 py-3 text-gray-500 text-sm">Nuk ka pajisje</div>}
                </div>
              )}
            </div>
            {isSuperAdmin && selectedCompanyId && (
              <button onClick={openAddModal} className="px-5 py-2.5 rounded-xl text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2 shrink-0">
                <span className="text-lg leading-none">+</span> {t('addDevice')}
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 p-4">
          {/* Web: grid me të gjitha pajisjet */}
          <div className="grid grid-cols-6 gap-2 h-full content-start">
            {devices.map((device, index) => (
              <div
                key={device.id}
                data-testid={`device-card-${device.id}`}
                onClick={() => {
                  if (device.type === 's475') navigate(`/devices/s475/${device.id}`);
                  else if (device.type === 'datalogger') navigate(`/devices/${device.id}`);
                  else if (device.type === 'inverter') navigate(`/devices/${device.data_logger_id}/inverter/${device.id}`);
                  else if (device.type === 'energymeter') navigate(`/devices/${device.data_logger_id || 'standalone'}/energy-meter/${device.id}`);
                }}
                onMouseDown={() => handleTouchStart(device)}
                onMouseUp={handleTouchEnd}
                onMouseLeave={handleTouchEnd}
                className="relative bg-white dark:bg-[#1e293b] rounded-lg p-2 cursor-pointer hover:scale-[1.02] transition flex flex-col justify-between min-h-0"
                style={{ height: 'calc((100vh - 200px) / 3 - 6px)' }}
              >
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); if (device.type === 's475' && isSuperAdmin) openEditModal(device); else { setEdgePopupDevice(device); setShowEdgePopup(true); } }}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-gray-200 dark:bg-slate-600 hover:bg-gray-300 dark:hover:bg-slate-500 flex items-center justify-center text-gray-600 dark:text-gray-300 text-[10px] font-medium z-10"
                  aria-label="Të dhënat e pajisjes"
                >
                  ℹ
                </button>
                <div className="flex-1 min-h-0 rounded overflow-hidden bg-white dark:bg-[#1e293b] flex items-center justify-center mb-1">
                  <img src={getPhotoUrl(device)} alt={device.name || 'Paisje'} className="max-h-full max-w-full object-contain" onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }} />
                </div>

                <div className="min-h-0 shrink-0">
                  <h4 className="font-semibold text-xs text-gray-900 dark:text-white mb-0.5 truncate">
                    {device.type === 's475' ? (device.label || device.name) : device.type === 'inverter' ? (device.model || device.name || `Inverter-${index + 1}`) : device.type === 'energymeter' ? (device.name || device.model || `Energy Meter-${index + 1}`) : device.name}
                  </h4>
                  <div className="flex items-center gap-1 text-[10px] flex-wrap">
                    {(() => {
                      const style = getDeviceStatusStyle(device);
                      const statusLabel = getDeviceStatusText(device) || getDefaultStatusLabel(device);
                      return (
                        <span className={`font-medium flex items-center gap-0.5 ${style.textClass}`}>
                          <span className={`w-1 h-1 rounded-full shrink-0 ${style.dotClass}`}></span>
                          <span className="truncate" title={statusLabel}>{statusLabel}</span>
                        </span>
                      );
                    })()}
                    {device.type !== 's475' && (
                      <span className="text-gray-400 text-[9px] shrink-0">{device.type === 'energymeter' ? 'Energy Meter' : device.grid_status === 'ON_GRID' ? 'ON GRID' : 'OFF GRID'}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {devices.length === 0 && (
            <div className="text-center py-16 text-gray-500">
              <p className="text-lg">Nuk ka pajisje</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Layout>
      {isMobile ? <MobileListView /> : <WebListView />}

      {/* Add/Edit Device Modal */}
      {showAddModal && (
        <div className={`fixed inset-0 bg-black/60 flex items-center justify-center z-50 ${isMobile ? 'p-2' : 'p-4'}`}>
          {isMobile ? (
            /* MOBILE: S475 – grid 2 kolona (port, lat/lon, distanca), pa scroll; footer Ruaj gjithmonë i dukshëm */
            (() => {
              const compactForm = addDeviceType === 'datalogger' || addDeviceType === 'energymeter';
              const isS475 = addDeviceType === 's475';
              const fieldGap = compactForm ? 'space-y-3' : isS475 ? 'space-y-2' : 'space-y-4';
              const panelPy = compactForm ? 'py-1.5' : 'py-2';
              const inputH = compactForm ? 28 : isS475 ? 28 : 32;
              const photoMb = compactForm ? 'mb-2' : 'mb-4';
              const s475InputClass = 'w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white';
              return (
                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-sm flex flex-col max-h-[95vh] overflow-hidden">
                  <div className={`flex items-center justify-between px-2 ${panelPy} border-b border-gray-200 dark:border-slate-700 flex-shrink-0`}>
                    <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">{editingDevice ? t('editDevice') : t('addDevice')}</h3>
                    <button type="button" onClick={() => setShowAddModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-600 shrink-0" aria-label={t('close')}>✕</button>
                  </div>
                  <div className={`px-2 ${panelPy} ${fieldGap} overflow-y-auto min-h-0 flex-1`} style={isS475 ? { maxHeight: '55vh' } : undefined}>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('typeRequired')}</label>
                      <select value={addDeviceType} onChange={(e) => {
                        const v = e.target.value;
                        setAddDeviceType(v);
                        if (!editingDevice && (v === 'datalogger' || v === 'inverter')) setFormData((prev) => ({ ...prev, brand: 'Huawei' }));
                        if (!editingDevice && v === 'energymeter') setFormData((prev) => ({ ...prev, brand: '' }));
                      }} disabled={!!editingDevice}
                        style={{ height: `${inputH}px` }}
                        className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white">
                        <option value="">{t('selectType')}</option>
                        <option value="s475">DIO IOT Device</option>
                      </select>
                    </div>
                    {addDeviceType === 's475' && (
                      <>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Lokacioni</label>
                          <input type="text" value={formData.location_name} onChange={(e) => setFormData({ ...formData, location_name: e.target.value })}
                            style={{ height: `${inputH}px` }}
                            className={s475InputClass} placeholder="Mitrovicë" />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('s475DeviceIdLabel')}</label>
                            <input type="text" inputMode="numeric" value={formData.s475_id} onChange={(e) => setFormData({ ...formData, s475_id: e.target.value })}
                              style={{ height: `${inputH}px` }}
                              className={s475InputClass} placeholder="1" disabled={!!editingDevice} />
                            <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">{t('s475IdIsAlsoModbusUnit')}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Distanca (m)</label>
                            <input type="number" min={0} step={10} value={formData.checkin_radius_meters ?? ''} onChange={(e) => setFormData({ ...formData, checkin_radius_meters: e.target.value })}
                              style={{ height: `${inputH}px` }}
                              className={s475InputClass} placeholder="100" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Porti në server</label>
                            <input type="number" min={1} value={formData.http_port} onChange={(e) => setFormData({ ...formData, http_port: e.target.value === '' ? '' : Number(e.target.value) })}
                              style={{ height: `${inputH}px` }}
                              className={s475InputClass} placeholder="8081" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Porti i paisjes</label>
                            <input type="number" min={1} value={formData.port} onChange={(e) => setFormData({ ...formData, port: e.target.value === '' ? '' : Number(e.target.value) })}
                              style={{ height: `${inputH}px` }}
                              className={s475InputClass} placeholder="801" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Gjatësia (lon)</label>
                            <input type="text" inputMode="decimal" value={formData.lng} onChange={(e) => setFormData({ ...formData, lng: e.target.value })}
                              style={{ height: `${inputH}px` }}
                              className={s475InputClass} placeholder="20.86" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Gjerësia (lat)</label>
                            <input type="text" inputMode="decimal" value={formData.lat} onChange={(e) => setFormData({ ...formData, lat: e.target.value })}
                              style={{ height: `${inputH}px` }}
                              className={s475InputClass} placeholder="42.88" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('generatorLitersPerHour')}</label>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={formData.generator_liters_per_hour}
                              onChange={(e) => setFormData({ ...formData, generator_liters_per_hour: e.target.value })}
                              style={{ height: `${inputH}px` }}
                              className={s475InputClass}
                              placeholder="10"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('generatorTrackingDin')}</label>
                            <select
                              value={formData.generator_tracking_din}
                              onChange={(e) => setFormData({ ...formData, generator_tracking_din: e.target.value })}
                              style={{ height: `${inputH}px` }}
                              className={s475InputClass}
                            >
                              <option value="DIN0">{t('generatorTrackingDin0')}</option>
                              <option value="DIN1">{t('generatorTrackingDin1')}</option>
                            </select>
                          </div>
                        </div>
                      </>
                    )}
                    {addDeviceType && addDeviceType !== 's475' && (<>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('brand')}</label>
                        <select value={formData.brand} onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                          style={{ height: `${inputH}px` }}
                          className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white">
                          <option value="">{t('selectBrand')}</option>
                          {effectiveBrandOptions.map((b) => <option key={b} value={b}>{b}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('model')}</label>
                        <input type="text" value={formData.model} onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                          style={{ height: `${inputH}px` }}
                          className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" placeholder={t('model')} />
                      </div>
                      {addDeviceType === 'datalogger' ? (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('ipAddress')}</label>
                          <input type="text" value={formData.ip_address} onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                            style={{ height: `${inputH}px` }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" placeholder="192.168.1.100" />
                        </div>
                      ) : addDeviceType === 'inverter' && getCompanyDataLoggers().length > 0 ? (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('dataLoggerRequired')}</label>
                          <select value={formData.data_logger_id} onChange={(e) => setFormData({ ...formData, data_logger_id: e.target.value })}
                            style={{ height: `${inputH}px` }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white">
                            <option value="">{t('select')}</option>
                            {getCompanyDataLoggers().map(dl => (<option key={dl.id} value={dl.id}>{dl.name}</option>))}
                          </select>
                        </div>
                      ) : null}
                      {addDeviceType === 'inverter' && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('powerKw')}</label>
                          <input type="number" min={1} step={1} value={formData.power_kw} onChange={(e) => setFormData({ ...formData, power_kw: e.target.value })}
                            style={{ height: `${inputH}px` }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white"
                            placeholder="0" />
                        </div>
                      )}
                      {addDeviceType === 'energymeter' && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('ipAddress')}</label>
                          <input type="text" value={formData.ip_address} onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                            style={{ height: `${inputH}px` }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" placeholder="192.168.1.100" />
                        </div>
                      )}
                      {(addDeviceType === 'datalogger' || addDeviceType === 'energymeter') && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('port')}</label>
                          <input type="number" min={1} max={65535} value={formData.port} onChange={(e) => setFormData({ ...formData, port: e.target.value === '' ? '' : Number(e.target.value) })}
                            style={{ height: `${inputH}px` }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" placeholder="502" />
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('unitId')}</label>
                        <input type="number" min={0} value={formData.unit_id} onChange={(e) => setFormData({ ...formData, unit_id: e.target.value === '' ? '' : Number(e.target.value) })}
                          style={{ height: `${inputH}px` }}
                          className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white"
                          placeholder={addDeviceType === 'datalogger' ? '0' : addDeviceType === 'inverter' ? '12' : '1'} />
                      </div>
                      <div className={photoMb}>
                        <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => {
                          const f = e.target.files?.[0] || null;
                          setPhotoFile(f);
                          photoFileRef.current = f;
                        }} className="hidden" />
                        <button type="button" onClick={() => fileInputRef.current?.click()}
                          style={{ height: `${inputH}px` }}
                          className="w-full px-2 text-sm border border-dashed border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-500 dark:text-gray-400">
                          {photoFile ? `📷 ${photoFile.name.slice(0, 20)}` : t('uploadPhotoShort')}
                        </button>
                      </div>
                    </>)}
                  </div>
                  <div className={`flex gap-2 px-2 ${panelPy} border-t border-gray-200 dark:border-slate-700 flex-shrink-0`}>
                    {editingDevice && (
                      <button onClick={handleDeleteDevice} disabled={saving} style={{ height: '32px' }} className="flex-1 min-w-0 bg-red-500 text-white rounded text-sm font-medium disabled:opacity-50">{t('delete')}</button>
                    )}
                    <button onClick={() => setShowAddModal(false)} style={{ height: '32px' }} className="flex-1 min-w-0 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded text-sm font-medium">{t('cancel')}</button>
                    <button onClick={handleSaveDevice} disabled={!addDeviceType || saving}
                      style={{ height: '32px' }} className="flex-1 min-w-0 bg-green-500 text-white rounded text-sm font-medium disabled:opacity-50">{saving ? '...' : t('save')}</button>
                  </div>
                </div>
              );
            })()
          ) : (
            /* WEB: 2 column layout - compact fields */
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-xl flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-slate-700">
                <h3 className="text-base font-bold text-gray-900 dark:text-white">{editingDevice ? t('editDevice') : t('addDevice')}</h3>
                <button onClick={() => setShowAddModal(false)} className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600"><span className="text-gray-500 dark:text-gray-400">✕</span></button>
              </div>
              <div className="p-4">
                {/* Web: fillim vetëm zgjedhja e llojit (njëlloj si mobile), pastaj fushat e tjera */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{t('deviceTypeRequired')}</label>
                  <select value={addDeviceType} onChange={(e) => {
                    const v = e.target.value;
                    setAddDeviceType(v);
                    if (!editingDevice && (v === 'datalogger' || v === 'inverter')) setFormData((prev) => ({ ...prev, brand: 'Huawei' }));
                    if (!editingDevice && v === 'energymeter') setFormData((prev) => ({ ...prev, brand: '' }));
                  }} disabled={!!editingDevice}
                    style={{ height: '32px' }}
                    className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white">
                    <option value="">{t('selectType')}</option>
                    <option value="s475">DIO IOT Device</option>
                  </select>
                </div>
                {addDeviceType === 's475' && (
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">Lokacioni</label>
                      <input type="text" value={formData.location_name} onChange={(e) => setFormData({ ...formData, location_name: e.target.value })}
                        style={{ height: '32px' }}
                        className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" placeholder="Mitrovicë" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{t('s475DeviceIdLabel')}</label>
                      <input type="text" inputMode="numeric" value={formData.s475_id} onChange={(e) => setFormData({ ...formData, s475_id: e.target.value })}
                        style={{ height: '32px' }}
                        className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" placeholder="1" disabled={!!editingDevice} />
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">{t('s475IdIsAlsoModbusUnit')}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">Rrezja për check-in (m)</label>
                      <input type="number" min={0} step={10} value={formData.checkin_radius_meters ?? ''} onChange={(e) => setFormData({ ...formData, checkin_radius_meters: e.target.value })}
                        style={{ height: '32px' }}
                        className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" placeholder="100" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">Porti në server</label>
                      <input type="number" min={1} value={formData.http_port} onChange={(e) => setFormData({ ...formData, http_port: e.target.value === '' ? '' : Number(e.target.value) })}
                        style={{ height: '32px' }}
                        className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" placeholder="8081" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">Porti i paisjes</label>
                      <input type="number" min={1} value={formData.port} onChange={(e) => setFormData({ ...formData, port: e.target.value === '' ? '' : Number(e.target.value) })}
                        style={{ height: '32px' }}
                        className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" placeholder="801" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">Gjatësia (lon)</label>
                      <input type="text" inputMode="decimal" value={formData.lng} onChange={(e) => setFormData({ ...formData, lng: e.target.value })}
                        style={{ height: '32px' }}
                        className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" placeholder="20.86" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">Gjerësia (lat)</label>
                      <input type="text" inputMode="decimal" value={formData.lat} onChange={(e) => setFormData({ ...formData, lat: e.target.value })}
                        style={{ height: '32px' }}
                        className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" placeholder="42.88" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{t('generatorLitersPerHour')}</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formData.generator_liters_per_hour}
                        onChange={(e) => setFormData({ ...formData, generator_liters_per_hour: e.target.value })}
                        style={{ height: '32px' }}
                        className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white"
                        placeholder="10"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{t('generatorTrackingDin')}</label>
                      <select
                        value={formData.generator_tracking_din}
                        onChange={(e) => setFormData({ ...formData, generator_tracking_din: e.target.value })}
                        style={{ height: '32px' }}
                        className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white"
                      >
                        <option value="DIN0">{t('generatorTrackingDin0')}</option>
                        <option value="DIN1">{t('generatorTrackingDin1')}</option>
                      </select>
                    </div>
                  </div>
                )}
                {addDeviceType && addDeviceType !== 's475' && (
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    {/* Kolona e majtë */}
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{t('brand')}</label>
                        <select value={formData.brand} onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                          style={{ height: '32px' }}
                          className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white">
                          <option value="">{t('selectBrand')}</option>
                          {effectiveBrandOptions.map((b) => <option key={b} value={b}>{b}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{t('model')}</label>
                        <input type="text" value={formData.model} onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                          style={{ height: '32px' }}
                          className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" placeholder={t('model')} />
                      </div>
                      {addDeviceType === 'datalogger' ? (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{t('ipAddress')}</label>
                          <input type="text" value={formData.ip_address} onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                            style={{ height: '32px' }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" placeholder="192.168.1.100" />
                        </div>
                      ) : addDeviceType === 'inverter' && getCompanyDataLoggers().length > 0 ? (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{t('dataLoggerRequired')}</label>
                          <select value={formData.data_logger_id} onChange={(e) => setFormData({ ...formData, data_logger_id: e.target.value })}
                            style={{ height: '32px' }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white">
                            <option value="">{t('select')}</option>
                            {getCompanyDataLoggers().map(dl => (<option key={dl.id} value={dl.id}>{dl.name}</option>))}
                          </select>
                        </div>
                      ) : null}
                      {addDeviceType === 'inverter' && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{t('powerKw')}</label>
                          <input type="number" min={1} step={1} value={formData.power_kw} onChange={(e) => setFormData({ ...formData, power_kw: e.target.value })}
                            style={{ height: '32px' }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white"
                            placeholder="0" />
                        </div>
                      )}
                      {addDeviceType === 'energymeter' && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{t('ipAddress')}</label>
                          <input type="text" value={formData.ip_address} onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                            style={{ height: '32px' }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" placeholder="192.168.1.100" />
                        </div>
                      )}
                    </div>
                    {/* Kolona e djathtë */}
                    <div className="space-y-2">
                      {(addDeviceType === 'datalogger' || addDeviceType === 'energymeter') && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{t('port')}</label>
                          <input type="number" min={1} max={65535} value={formData.port} onChange={(e) => setFormData({ ...formData, port: e.target.value === '' ? '' : Number(e.target.value) })}
                            style={{ height: '32px' }}
                            className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" placeholder="502" />
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{t('unitId')}</label>
                        <input type="number" min={0} value={formData.unit_id} onChange={(e) => setFormData({ ...formData, unit_id: e.target.value === '' ? '' : Number(e.target.value) })}
                          style={{ height: '32px' }}
                          className="w-full px-2 text-sm border border-gray-300 dark:border-slate-600 rounded bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white"
                          placeholder={addDeviceType === 'datalogger' ? '0' : addDeviceType === 'inverter' ? '12' : '1'} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">{t('photo')}</label>
                        <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => {
                          const file = e.target.files?.[0] || null;
                          setPhotoFile(file);
                          photoFileRef.current = file;
                          // #region agent log
                          fetch('http://127.0.0.1:7244/ingest/d43d6308-ec48-474e-90c9-52e63fae8135', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'Devices.js:fileInputChange', message: 'File input onChange', data: { filesLength: e.target.files?.length, fileName: file?.name }, timestamp: Date.now(), sessionId: 'debug-session', hypothesisId: 'H3' }) }).catch(() => { });
                          // #endregion
                        }} className="hidden" />
                        <button type="button" onClick={() => {
                          // #region agent log
                          fetch('http://127.0.0.1:7244/ingest/d43d6308-ec48-474e-90c9-52e63fae8135', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'Devices.js:fotoButtonClick', message: 'Foto button clicked', data: { refExists: !!fileInputRef.current }, timestamp: Date.now(), sessionId: 'debug-session', hypothesisId: 'H2' }) }).catch(() => { });
                          // #endregion
                          fileInputRef.current?.click();
                        }}
                          style={{ height: '36px' }}
                          className="w-full px-3 text-sm font-medium rounded-lg border-2 border-dashed border-cyan-500 dark:border-cyan-400 bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 hover:bg-cyan-100 dark:hover:bg-cyan-900/50 hover:border-cyan-600 dark:hover:border-cyan-300 transition-colors cursor-pointer flex items-center justify-center gap-2">
                          <span aria-hidden>📷</span>
                          <span>{photoFile ? photoFile.name : t('selectFile')}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex gap-3 px-4 py-3 border-t border-gray-200 dark:border-slate-700">
                {editingDevice && (
                  <button onClick={handleDeleteDevice} disabled={saving} style={{ height: '32px' }} className="flex-1 bg-red-500 text-white rounded font-medium hover:bg-red-600 disabled:opacity-50 text-sm">{t('delete')}</button>
                )}
                <button onClick={() => setShowAddModal(false)} style={{ height: '32px' }} className="flex-1 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded font-medium hover:bg-gray-200 dark:hover:bg-slate-600 text-sm">{t('cancel')}</button>
                <button onClick={handleSaveDevice} disabled={!addDeviceType || saving}
                  style={{ height: '32px' }} className="flex-1 bg-green-500 text-white rounded font-medium hover:bg-green-600 disabled:opacity-50 text-sm">{saving ? t('saving') : t('save')}</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Popup të dhënat e pajisjes (Edge / Raspberry) */}
      {showEdgePopup && edgePopupDevice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={() => setShowEdgePopup(false)}>
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-sm w-full p-4 border border-gray-200 dark:border-slate-600" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-gray-900 dark:text-white text-sm">{t('deviceData')}</h4>
              <button type="button" onClick={() => setShowEdgePopup(false)} className="w-7 h-7 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-600 text-sm" aria-label={t('close')}>✕</button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 truncate">{edgePopupDevice.name || edgePopupDevice.serial_number}</p>
            <dl className="space-y-2 text-sm">
              {edgePopupDevice.ip_address != null && edgePopupDevice.ip_address !== '' && (
                <div className="flex justify-between gap-2"><dt className="text-gray-500 dark:text-gray-400">IP</dt><dd className="font-mono text-gray-900 dark:text-white">{edgePopupDevice.ip_address}</dd></div>
              )}
              {edgePopupDevice.port != null && (
                <div className="flex justify-between gap-2"><dt className="text-gray-500 dark:text-gray-400">{t('port')}</dt><dd className="font-mono text-gray-900 dark:text-white">{edgePopupDevice.port}</dd></div>
              )}
              {edgePopupDevice.unit_id != null && (
                <div className="flex justify-between gap-2"><dt className="text-gray-500 dark:text-gray-400">{t('unitId')}</dt><dd className="font-mono text-gray-900 dark:text-white">{edgePopupDevice.unit_id}</dd></div>
              )}
              {edgePopupDevice.edge_device_id != null && (
                <div className="flex justify-between gap-2"><dt className="text-gray-500 dark:text-gray-400">{t('idOnRaspberry')}</dt><dd className="font-mono text-gray-900 dark:text-white">{edgePopupDevice.edge_device_id}</dd></div>
              )}
              {edgePopupDevice.power_kw != null && (
                <div className="flex justify-between gap-2"><dt className="text-gray-500 dark:text-gray-400">{t('power')}</dt><dd className="font-mono text-gray-900 dark:text-white">{edgePopupDevice.power_kw} kW</dd></div>
              )}
              {!edgePopupDevice.ip_address && edgePopupDevice.port == null && edgePopupDevice.unit_id == null && edgePopupDevice.edge_device_id == null && (
                <p className="text-gray-500 dark:text-gray-400 text-xs">{t('noEdgeData')}</p>
              )}
            </dl>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Devices;